/**
 * DisqualifyDialog — disqualify a team, choosing how its matches are treated:
 *   • Walkovers (default): results so far stand; unplayed matches go to the opponents.
 *   • Remove its results: all its league/group results are taken out of the
 *     standings (as if it never entered); unplayed matches are cancelled.
 * Unplayed knockout matches go to the opponent either way. Shows what each
 * choice will change before confirming. Backed by team-setup /disqualify.
 */
import { useEffect, useState } from 'react';
import { axiosInstance } from '../../config/axios';
import { Modal } from '../../pages/admin/_shared';

const MODES = [
  {
    id: 'walkover',
    title: 'Walkovers to the opponents',
    note: 'Recommended. Results already played stand; every unplayed match is awarded to the opponent.',
  },
  {
    id: 'expunge',
    title: 'Remove its results',
    note: 'As if the team never entered: all its league results leave the table; unplayed matches are cancelled. Best early in the tournament.',
  },
];

function impact(p, mode) {
  if (!p) return null;
  const lines = [];
  if (mode === 'walkover') {
    if (p.league_unplayed) lines.push(`${p.league_unplayed} unplayed match(es) become walkovers to the opponents`);
    if (p.league_played) lines.push(`${p.league_played} played match(es) keep their results`);
  } else {
    if (p.league_played) lines.push(`${p.league_played} played match(es) removed from the standings (for both sides)`);
    if (p.league_unplayed) lines.push(`${p.league_unplayed} unplayed match(es) cancelled`);
  }
  if (p.knockout_unplayed) lines.push(`${p.knockout_unplayed} knockout match(es) go to the opponent`);
  if (p.in_progress) lines.push(`${p.in_progress} match(es) in progress are left as they are`);
  lines.push('The team is listed last in the standings and can\'t qualify for knockouts');
  return lines;
}

export default function DisqualifyDialog({ eventId, team, busy, onClose, onConfirm }) {
  const [reason, setReason] = useState('');
  const [mode, setMode] = useState('walkover');
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    axiosInstance.get(`/v1/events/${eventId}/team-setup/teams/${team.id}/disqualify-preview`)
      .then((r) => setPreview(r.data)).catch(() => setPreview(null));
  }, [eventId, team.id]);

  const valid = reason.trim().length >= 3;
  const lines = impact(preview, mode);
  const inputCls = 'w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-white';

  return (
    <Modal title={`Disqualify ${team.team_name}`} onClose={onClose}>
      <div className="space-y-4" data-testid="disqualify-dialog">
        <div>
          <label className="block text-xs font-bold text-gray-500 mb-1">Reason (as per tournament rules) *</label>
          <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} className={inputCls}
            placeholder="e.g. Fielded an unregistered player" data-testid="dq-reason" />
        </div>

        <div className="space-y-2">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Its matches</p>
          {MODES.map((m) => (
            <label key={m.id}
              className={`flex items-start gap-2 p-3 rounded-xl border-2 cursor-pointer ${mode === m.id ? 'border-[#2B3A67]' : 'border-gray-100'}`}>
              <input type="radio" name="dq-mode" checked={mode === m.id} onChange={() => setMode(m.id)}
                className="mt-1" data-testid={`dq-mode-${m.id}`} />
              <span>
                <span className="font-bold text-sm block">{m.title}</span>
                <span className="text-xs text-gray-500">{m.note}</span>
              </span>
            </label>
          ))}
        </div>

        {lines && (
          <ul className="text-xs text-gray-600 bg-gray-50 rounded-xl p-3 list-disc pl-6 space-y-1" data-testid="dq-impact">
            {lines.map((l) => <li key={l}>{l}</li>)}
          </ul>
        )}
        <p className="text-xs text-gray-400">Reinstating the team later puts its matches back, unless they've been changed since.</p>

        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-3 bg-gray-200 text-gray-700 font-bold rounded-2xl">Cancel</button>
          <button onClick={() => onConfirm({ reason: reason.trim(), mode })} disabled={!valid || busy}
            className="flex-1 py-3 bg-red-500 text-white font-extrabold rounded-2xl disabled:opacity-50"
            data-testid="dq-confirm">Disqualify</button>
        </div>
      </div>
    </Modal>
  );
}
